<script setup>
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import InternalChatThreadReply from './InternalChatThreadReply.vue';
import { useInternalChatHistoryScroll } from './useInternalChatHistoryScroll';
import { useInternalChatMessageFocus } from './useInternalChatMessageFocus';

const props = defineProps({
  thread: { type: Object, required: true },
  currentUserId: { type: Number, default: null },
  saveMessage: { type: Function, required: true },
  members: { type: Object, default: () => ({ users: [], teams: [] }) },
  canMentionAll: { type: Boolean, default: false },
  canManageMessages: { type: Boolean, default: false },
  canEditOwnMessages: { type: Boolean, default: true },
  actionError: { type: String, default: '' },
  historyState: { type: Object, required: true },
  loadOlder: { type: Function, required: true },
  loadNewer: { type: Function, required: true },
  focusedMessageId: { type: Number, default: null },
  refresh: { type: Function, required: true },
});
const emit = defineEmits(['delete']);
const { t } = useI18n();
const { viewport, content, onScroll, preserve } = useInternalChatHistoryScroll(
  () => props.historyState.generation
);
const { focusMessage } = useInternalChatMessageFocus(viewport);
defineExpose({ focusMessage });
const load = async (action = props.loadOlder) => {
  try {
    await preserve(action);
  } catch {
    // The thread store retains the page, cursor and retry error.
  }
};
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <div class="flex shrink-0 flex-col gap-1 px-4 py-2">
      <p
        class="mb-0 text-xs text-n-slate-11"
        data-testid="internal-chat-thread-total"
      >
        {{
          t('INTERNAL_CHAT.REPLIES', {
            count: thread.parent.replies_count || 0,
          })
        }}
      </p>
      <Button
        v-if="thread.meta.has_more"
        type="button"
        variant="ghost"
        color="slate"
        size="sm"
        :label="t('INTERNAL_CHAT.LOAD_OLDER_REPLIES')"
        :is-loading="historyState.isLoadingOlder"
        :disabled="
          historyState.isLoadingOlder ||
          historyState.isLoadingNewer ||
          historyState.isRefreshing ||
          historyState.isOpening
        "
        data-testid="internal-chat-thread-older"
        @click="load()"
      />
      <Button
        v-if="thread.meta.has_more_after"
        type="button"
        variant="ghost"
        color="slate"
        size="sm"
        :label="t('INTERNAL_CHAT.LOAD_NEWER_REPLIES')"
        :is-loading="historyState.isLoadingNewer"
        :disabled="
          historyState.isLoadingOlder ||
          historyState.isLoadingNewer ||
          historyState.isRefreshing ||
          historyState.isOpening
        "
        data-testid="internal-chat-thread-newer"
        @click="load(loadNewer)"
      />
      <Button
        v-if="historyState.error && !thread.meta.has_more"
        type="button"
        variant="ghost"
        color="slate"
        size="sm"
        :label="t('INTERNAL_CHAT.RETRY')"
        :disabled="historyState.isRefreshing || historyState.isOpening"
        @click="load(refresh)"
      />
      <p
        v-if="historyState.isOpening"
        class="mb-0 text-xs text-n-slate-11"
        role="status"
      >
        {{ t('INTERNAL_CHAT.LOADING_MESSAGES') }}
      </p>
      <p
        v-if="historyState.error || actionError"
        class="mb-0 text-xs text-n-ruby-11"
        role="alert"
      >
        {{ actionError || t('INTERNAL_CHAT.THREAD_LOAD_FAILED') }}
      </p>
    </div>
    <div
      ref="viewport"
      class="min-h-0 flex-1 overflow-y-auto p-4 [overflow-anchor:none]"
      data-testid="internal-chat-thread-list"
      @scroll="onScroll"
    >
      <div ref="content" class="grid gap-2">
        <InternalChatThreadReply
          v-for="reply in thread.replies"
          :key="reply.id"
          :message="reply"
          :class="
            reply.id === focusedMessageId
              ? 'rounded-lg ring-2 ring-n-brand'
              : ''
          "
          :current-user-id="currentUserId"
          :save-message="saveMessage"
          :members="members"
          :can-mention-all="canMentionAll"
          :can-manage-messages="canManageMessages"
          :can-edit-own-messages="canEditOwnMessages"
          tabindex="-1"
          @delete="emit('delete', $event)"
        />
      </div>
    </div>
  </div>
</template>
